import { memo, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useInvite } from '@/hooks/useInvite';
import { InviteCardSkeleton } from '@/components/LoadingSkeleton';
import { getThemeStyles } from '@/utils/theme';
import styles from './StoryPage.module.css';

type StoryData = {
  text?: string;
  images?: string[];
};

/**
 * Страница истории приглашения
 *
 * Ответственность:
 * - Загрузка данных приглашения
 * - Отображение истории из customFields.story
 * - Обработка состояний загрузки/ошибок
 * - Навигация назад к приглашению
 */
const StoryPage = memo(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { invite, loading, error } = useInvite(id);

  const defaultThemeStyles = useMemo(() => getThemeStyles(), []);

  const handleBack = () => {
    if (id) {
      navigate(`/invite/${id}`);
    } else {
      navigate('/');
    }
  };

  // Парсим story данные
  const storyData = useMemo<StoryData | null>(() => {
    const { story } = invite?.customFields || {};
    if (!story) {
      return null;
    }

    // Если story - строка
    if (typeof story === 'string') {
      return { text: story };
    }

    // Если story - объект
    if (typeof story === 'object' && story !== null) {
      const storyObj = story as { text?: unknown; images?: unknown };
      return {
        text: typeof storyObj.text === 'string' ? storyObj.text : undefined,
        images: Array.isArray(storyObj.images) ? storyObj.images as string[] : undefined,
      };
    }

    return null;
  }, [invite?.customFields]);

  if (loading) {
    return (
      <div className={`${defaultThemeStyles.container} ${styles.container}`}>
        <div className={styles.skeletonContainer}>
          <InviteCardSkeleton />
        </div>
      </div>
    );
  }

  if (error || !invite) {
    return (
      <div className={`${defaultThemeStyles.container} ${styles.container}`}>
        <div className={`${defaultThemeStyles.error || ''} ${styles.error}`}>
          <h2 className={`${defaultThemeStyles.errorTitle || ''} ${styles.errorTitle}`}>
            Ошибка
          </h2>
          <p>{error || 'Приглашение не найдено'}</p>
          <button type="button" onClick={handleBack} className={styles.backButton}>
            Назад
          </button>
        </div>
      </div>
    );
  }

  if (!storyData || (!storyData.text && !storyData.images?.length)) {
    return (
      <div className={`${defaultThemeStyles.container} ${styles.container}`}>
        <div className={styles.storySection}>
          <div className={styles.storyContent}>
            <button type="button" onClick={handleBack} className={styles.backButton}>
              ← Назад
            </button>
            <h1 className={styles.storyTitle}>История</h1>
            <p className={styles.storyText}>История пока не добавлена</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${defaultThemeStyles.container} ${styles.container}`}>
      <div className={styles.storySection}>
        <div className={styles.storyContent}>
          <button type="button" onClick={handleBack} className={styles.backButton}>
            ← Назад
          </button>
          <h1 className={styles.storyTitle}>Наша история</h1>
          {storyData.text && (
            <p className={styles.storyText}>{storyData.text}</p>
          )}
          {storyData.images && storyData.images.length > 0 && (
            <div className={styles.storyImages}>
              {storyData.images.map((imageUrl) => (
                <img
                  key={imageUrl}
                  src={imageUrl}
                  alt="История"
                  loading="lazy"
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

StoryPage.displayName = 'StoryPage';

export default StoryPage;
